// src/components/contract-types/ContractTypeView.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Layers,
  FileText,
  DollarSign,
  Clock,
  CheckCircle,
  Shield,
  Tag,
  Calendar,
  Settings,
  AlertCircle,
} from 'lucide-react';
import MasterDataService from '../../services/masterDataService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import { useToast, ToastContainer } from '../common/Toast';

const ContractTypeView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toasts, removeToast } = useToast();

  const [contractType, setContractType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadContractType();
  }, [id]);

  const loadContractType = async () => {
    try {
      setLoading(true);
      const res = await MasterDataService.getById(id);
      const item = res.data;

      let metadata = {};
      if (item.metadata) {
        metadata =
          item.metadata instanceof Map
            ? Object.fromEntries(item.metadata)
            : item.metadata;
      }

      setContractType({
        id: item._id || item.id,
        name: item.label || item.code,
        description: item.description,
        status: item.isActive !== false ? 'Active' : 'Inactive',
        category: metadata.category || 'Other',
        pricingModel: metadata.pricingModel || 'Fixed-Price',
        baseAmount: metadata.baseAmount || 0,
        currency: metadata.currency || 'USD',
        duration: metadata.duration || '—',
        approvalLevel: metadata.approvalLevel || 'Manager',
        requiresLegalReview: metadata.requiresLegalReview,
        requiresFinanceReview: metadata.requiresFinanceReview,
        autoRenewal: metadata.autoRenewal,
        noticePeriod: metadata.noticePeriod || 0,
        templates: metadata.templates || 0,
        contracts: metadata.contracts || 0,
        tags: metadata.tags || [],
        features: metadata.features || [],
        complianceRequirements: metadata.complianceRequirements || [],
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      });
    } catch {
      setError('Failed to load contract type');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : '—';

  const formatCurrency = (v, c) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: c,
    }).format(v);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[var(--primary-color)]" />
      </div>
    );
  }

  if (error || !contractType) {
    return (
      <div className="flex h-screen items-center justify-center text-center">
        <AlertCircle size={44} className="mx-auto mb-3 text-red-500" />
        <p className="mb-4 text-gray-500">{error}</p>
        <button
          onClick={() => navigate('/contracts/types/management')}
          className="rounded-lg bg-[var(--primary-color)] px-6 py-3 text-white"
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <>
      <PageBreadcrumb />

      {/* Header */}
      <div className="mb-6 flex items-center justify-between px-8 mt-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {contractType.name}
          </h1>
          <p className="text-sm text-gray-500">
            {contractType.category} • {contractType.status}
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/contracts/types/management')}
            className="h-10 w-10 rounded-lg border bg-white"
          >
            <ArrowLeft size={16} className="mx-auto" />
          </button>
          <button
            onClick={() => navigate(`/contracts/types/configure/${contractType.id}?mode=edit`)}
            className="flex items-center gap-2 rounded-lg bg-[var(--primary-color)] px-5 text-sm text-white"
          >
            <Edit size={16} /> Edit
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mx-8 border-b bg-white">
        <div className="flex">
          {[
            { id: 'overview', label: 'Overview', icon: FileText },
            { id: 'financial', label: 'Financial', icon: DollarSign },
            { id: 'workflow', label: 'Workflow', icon: Settings },
            { id: 'metadata', label: 'Metadata', icon: Layers },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex gap-2 px-5 py-4 text-sm font-medium ${
                activeTab === id
                  ? 'border-b-2 border-[var(--primary-color)] text-[var(--primary-color)]'
                  : 'text-gray-500'
              }`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="mx-8 rounded-b-xl bg-white p-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 gap-6">
            <div className="rounded-xl border bg-slate-50 p-6">
              <h3 className="mb-4 flex items-center gap-2 font-semibold">
                <FileText size={16} /> Basic Info
              </h3>
              <Info label="Description" value={contractType.description} />
              <Info label="Category" value={contractType.category} />
              <Info label="Pricing Model" value={contractType.pricingModel} />
              <Info label="Approval Level" value={contractType.approvalLevel} />
            </div>

            <div className="rounded-xl border bg-slate-50 p-6">
              <h3 className="mb-4 flex items-center gap-2 font-semibold">
                <Calendar size={16} /> Dates
              </h3>
              <Info
                label="Created"
                value={formatDate(contractType.createdAt)}
              />
              <Info
                label="Last Updated"
                value={formatDate(contractType.updatedAt)}
              />
            </div>
          </div>
        )}

        {activeTab === 'financial' && (
          <div className="grid grid-cols-3 gap-6">
            <Stat
              label="Base Amount"
              value={formatCurrency(
                contractType.baseAmount,
                contractType.currency
              )}
            />
            <Stat label="Duration" value={contractType.duration} />
            <Stat
              label="Notice Period"
              value={`${contractType.noticePeriod} days`}
            />
          </div>
        )}

        {activeTab === 'workflow' && (
          <div className="grid grid-cols-3 gap-6">
            <Flag
              label="Legal Review"
              enabled={contractType.requiresLegalReview}
              icon={Shield}
            />
            <Flag
              label="Finance Review"
              enabled={contractType.requiresFinanceReview}
              icon={CheckCircle}
            />
            <Flag
              label="Auto Renewal"
              enabled={contractType.autoRenewal}
              icon={Clock}
            />
          </div>
        )}

        {activeTab === 'metadata' && (
          <div className="grid grid-cols-2 gap-6">
            <TagList title="Tags" items={contractType.tags} />
            <TagList title="Features" items={contractType.features} />
            <TagList
              title="Compliance"
              items={contractType.complianceRequirements}
            />
          </div>
        )}
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

// replace Info component with this (description goes to next line)

const Info = ({ label, value }) => (
  <div className="py-2">
    <span className="block text-xs uppercase text-gray-500 mb-1">{label}</span>
    <span className="block text-sm font-medium text-gray-800">
      {value || '—'}
    </span>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="rounded-xl border bg-slate-50 p-6">
    <p className="text-xs uppercase text-gray-500">{label}</p>
    <p className="mt-2 text-xl font-bold">{value}</p>
  </div>
);

const Flag = ({ label, enabled, icon: Icon }) => (
  <div className="rounded-xl border bg-slate-50 p-6 flex items-center gap-3">
    <Icon size={20} />
    <div>
      <div className="font-semibold">{label}</div>
      <div className={enabled ? 'text-emerald-600' : 'text-gray-400'}>
        {enabled ? 'Enabled' : 'Disabled'}
      </div>
    </div>
  </div>
);

const TagList = ({ title, items }) => (
  <div className="rounded-xl border bg-slate-50 p-6">
    <h3 className="mb-3 font-semibold">{title}</h3>
    {items?.length ? (
      <div className="flex flex-wrap gap-2">
        {items.map((t, i) => (
          <span
            key={i}
            className="rounded-full bg-blue-100 px-3 py-1 text-xs text-blue-700"
          >
            {t}
          </span>
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-400">None</p>
    )}
  </div>
);

export default ContractTypeView;